import React, { useState } from 'react';
import { useBackendApiService } from '../../services/backendApiService';
import useToast from '../../hooks/useToast';
import JsonEditor from './JsonEditor';

const RestCommandTool = () => {
  const backendApi = useBackendApiService();
  const toast = useToast();

  const [agentId, setAgentId] = useState('');
  const [method, setMethod] = useState('GET');
  const [url, setUrl] = useState('');
  const [headers, setHeaders] = useState({});
  const [body, setBody] = useState({});
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!agentId || !url) {
      toast.error('Agent ID and URL are required.');
      return;
    }

    setLoading(true);
    setError(null);
    setResponse(null);

    const payload = {
      agentId,
      method,
      url,
      headers: Object.keys(headers).length > 0 ? headers : undefined,
      body: Object.keys(body).length > 0 ? body : undefined,
    };

    try {
      const result = await backendApi.post('/test/send-rest-call', payload);
      setResponse(result);
      toast.success(`Command dispatched successfully. Command ID: ${result.commandId}`);
    } catch (err) {
      console.error('Failed to send REST command:', err);
      setError(err.message || 'An unknown error occurred.');
      toast.error(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-bold mb-4">Agent-Mediated REST Command</h2>
      <p className="text-sm text-gray-600 mb-4">
        This tool sends a REST command instruction to the backend, which relays it to the specified
        agent for execution. The result is not returned directly; only a command ID is provided.
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Agent ID</label>
          <input
            type="text"
            value={agentId}
            onChange={e => setAgentId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Enter agent ID"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">HTTP Method</label>
            <select
              value={method}
              onChange={e => setMethod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option>GET</option>
              <option>POST</option>
              <option>PUT</option>
              <option>DELETE</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
            <input
              type="text"
              value={url}
              onChange={e => setUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="https://api.example.com/data"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Headers (JSON)</label>
          <JsonEditor value={headers} onChange={setHeaders} height="100px" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Body (JSON)</label>
          <JsonEditor value={body} onChange={setBody} height="150px" />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center justify-center disabled:opacity-50"
        >
          {loading ? 'Dispatching...' : 'Dispatch Command'}
        </button>
      </form>

      {response && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold">Success</h3>
          <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto mt-2">
            {JSON.stringify(response, null, 2)}
          </pre>
        </div>
      )}

      {error && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-red-600">Error</h3>
          <pre className="bg-red-50 p-3 rounded text-xs text-red-800 overflow-auto mt-2">
            {error}
          </pre>
        </div>
      )}
    </div>
  );
};

export default RestCommandTool;
